import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Heart, Users, MessageCircle, BookOpen, Sparkles, ArrowRight } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({ matches: 0, quizzes: 0 });
  const [suggestedQuiz, setSuggestedQuiz] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [matchesRes, quizzesRes, resultsRes] = await Promise.all([
          axios.get(`${API_URL}/matches`),
          axios.get(`${API_URL}/quizzes`),
          axios.get(`${API_URL}/quizzes/results/me`)
        ]);
        
        setStats({
          matches: matchesRes.data.length,
          quizzes: resultsRes.data.length
        });

        // Find a quiz user hasn't taken
        const takenQuizIds = new Set(resultsRes.data.map(r => r.quiz_id));
        const untakenQuiz = quizzesRes.data.find(q => !takenQuizIds.has(q.id));
        if (untakenQuiz) {
          setSuggestedQuiz(untakenQuiz);
        }
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      }
    };

    fetchStats();
  }, []);

  const quickActions = [
    {
      icon: <Users className="w-6 h-6" strokeWidth={1.5} />,
      title: "Discover",
      description: "Find new compatible matches",
      path: "/discover",
      color: "bg-rose-50 text-rose-600"
    },
    {
      icon: <MessageCircle className="w-6 h-6" strokeWidth={1.5} />,
      title: "Messages",
      description: "Chat with your matches",
      path: "/matches",
      color: "bg-blue-50 text-blue-600"
    },
    {
      icon: <Sparkles className="w-6 h-6" strokeWidth={1.5} />,
      title: "Quizzes",
      description: "Improve your compatibility profile",
      path: "/quizzes",
      color: "bg-amber-50 text-amber-600"
    },
    {
      icon: <BookOpen className="w-6 h-6" strokeWidth={1.5} />,
      title: "Learn",
      description: "Educational content & tips",
      path: "/education",
      color: "bg-purple-50 text-purple-600"
    }
  ];

  return (
    <Layout>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Welcome Section */}
        <div className="animate-slideUp">
          <h1 className="text-4xl font-serif font-semibold mb-2">
            Welcome back, {user?.display_name}
          </h1>
          <p className="text-muted-foreground text-lg">
            Your journey to meaningful connections continues here.
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 stagger-children">
          <Card className="rounded-2xl border-border/50 shadow-soft">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Your Matches</p>
                  <p className="text-3xl font-serif font-semibold">{stats.matches}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-rose-50 flex items-center justify-center">
                  <Heart className="w-6 h-6 text-rose-500" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-border/50 shadow-soft">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Quizzes Taken</p>
                  <p className="text-3xl font-serif font-semibold">{stats.quizzes}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-amber-500" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-border/50 shadow-soft col-span-2">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Profile Completion</p>
                  <p className="text-3xl font-serif font-semibold">
                    {Math.round(((user?.bio ? 1 : 0) + (user?.profile_photo_url ? 1 : 0) + stats.quizzes) / 5 * 100)}%
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  className="rounded-full"
                  onClick={() => navigate('/profile')}
                  data-testid="edit-profile-btn"
                >
                  Edit Profile
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div>
          <h2 className="text-2xl font-serif font-medium mb-4">Quick Actions</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {quickActions.map((action, index) => (
              <Card 
                key={index}
                className="rounded-2xl border-border/50 shadow-soft cursor-pointer hover-lift transition-all"
                onClick={() => navigate(action.path)}
                data-testid={`quick-action-${action.title.toLowerCase()}`}
              >
                <CardContent className="pt-6">
                  <div className={`w-12 h-12 rounded-xl ${action.color} flex items-center justify-center mb-4`}>
                    {action.icon}
                  </div>
                  <h3 className="font-medium mb-1">{action.title}</h3>
                  <p className="text-sm text-muted-foreground">{action.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Suggested Quiz */}
        {suggestedQuiz && (
          <Card className="rounded-2xl border-primary/20 bg-primary/5 shadow-soft" data-testid="suggested-quiz-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl font-serif">
                <Sparkles className="w-5 h-5 text-primary" />
                Recommended Quiz
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h3 className="font-medium text-lg">{suggestedQuiz.title}</h3>
                  <p className="text-muted-foreground">{suggestedQuiz.description}</p>
                </div>
                <Button 
                  className="rounded-full shrink-0"
                  onClick={() => navigate(`/quizzes/${suggestedQuiz.id}`)}
                  data-testid="take-quiz-btn"
                >
                  Take Quiz
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Educational Tip */}
        <Card className="rounded-2xl border-border/50 shadow-soft bg-gradient-to-br from-purple-50 to-background">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center shrink-0">
                <BookOpen className="w-6 h-6 text-purple-600" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <h3 className="font-medium mb-1">Did you know?</h3>
                <p className="text-muted-foreground text-sm mb-3">
                  The Kama Sutra teaches that emotional connection enhances physical intimacy. 
                  Check out our educational content for tips on building deeper bonds.
                </p>
                <Button 
                  variant="link" 
                  className="p-0 h-auto text-purple-600"
                  onClick={() => navigate('/education')}
                  data-testid="explore-education-btn"
                >
                  Explore Educational Content
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default DashboardPage;
